import { forwardRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/utils/cn';
import { filterTriggerHover, filterTriggerFocus } from './filterTriggerStyles';
import { Tooltip } from './Tooltip';

/**
 * Select Component
 *
 * Dropdown select using Radix UI
 */

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
}

interface SelectProps {
  label?: string;
  error?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  size?: 'default' | 'sm';
  /** When true, adds a "clear" option that resets to empty string. Use clearLabel to customize text. */
  clearable?: boolean;
  /** Label for the clear option (default: placeholder text or "All") */
  clearLabel?: string;
}

// Special value used internally for the "clear" option
const CLEAR_VALUE = '__clear__';

/** Shared dropdown indicator for Select controls and matching filter menus. */
export const SelectChevron = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      error,
      placeholder = 'Select an option',
      options,
      value,
      onValueChange,
      disabled,
      className = '',
      size = 'default',
      clearable = false,
      clearLabel,
    },
    ref
  ) => {
    // Filter out any empty string values from options (Radix UI doesn't allow them)
    const filteredOptions = options.filter((opt) => opt.value !== '');

    // Handle value change, converting CLEAR_VALUE back to empty string
    const handleValueChange = (newValue: string) => {
      if (newValue === CLEAR_VALUE) {
        onValueChange?.('');
      } else {
        onValueChange?.(newValue);
      }
    };

    // Keep value as empty string (not undefined) to stay controlled
    // Radix UI will show placeholder when value is empty string
    const displayValue = value ?? '';

    const selectedLabel = filteredOptions.find((opt) => opt.value === displayValue)?.label;

    return (
      <div>
        {label && (
          <label className={`block font-medium text-gray-700 mb-1 ${size === 'sm' ? 'text-xs lg:text-sm' : 'text-sm'}`}>
            {label}
          </label>
        )}
        <SelectPrimitive.Root
          value={displayValue}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <Tooltip content={selectedLabel} side="top">
            <SelectPrimitive.Trigger
              ref={ref}
              className={cn(
              'w-full',
              // Match Input component sizing: px-4 py-3 text-[12px]. Mobile renders at 16px
              // to prevent iOS Safari zoom-on-focus; desktop keeps current sizing.
              size === 'sm' ? 'px-3 h-[34px] text-[16px] lg:h-auto lg:px-4 lg:py-3 lg:text-[12px]' : 'px-4 py-3 text-[16px] lg:text-[12px]',
              'border border-gray-200 rounded-lg',
              'text-gray-700 placeholder-gray-400',
              filterTriggerFocus,
              'data-[state=open]:border-primary-600',
              'transition-[border-color,background-color,color,opacity] duration-200',
              !disabled && filterTriggerHover,
              'disabled:bg-gray-50 disabled:cursor-not-allowed',
              'flex items-center justify-between',
              error ? 'border-danger' : '',
              className
            )}
            >
              {/* Note: Icons are rendered inside ItemText, which Radix copies to Value */}
              {/* Single line + ellipsis so long names never wrap inside the fixed-height trigger */}
              <span className="flex min-w-0 flex-1 whitespace-nowrap text-left [&>span]:block [&>span]:min-w-0 [&>span]:flex-1 [&>span]:overflow-hidden">
                <SelectPrimitive.Value placeholder={placeholder} />
              </span>
              <SelectPrimitive.Icon className="ml-2 flex-shrink-0">
                <SelectChevron />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
          </Tooltip>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className={cn(
                'overflow-hidden bg-white rounded-lg shadow-lg',
                'border border-gray-200',
                // Dropdown can be wider than trigger if needed
                'min-w-[var(--radix-select-trigger-width)]',
                'max-h-[300px]',
                // Use z-popover-top to ensure it appears above modal content
                'z-popover-top'
              )}
              position="popper"
              sideOffset={5}
            >
              <SelectPrimitive.Viewport className="p-1">
                {/* Clear option when clearable is enabled */}
                {clearable && (
                  <SelectPrimitive.Item
                    value={CLEAR_VALUE}
                    className={cn(
                      'px-3 py-2 rounded-md max-sm:min-h-[44px] max-sm:flex max-sm:items-center',
                      'text-sm text-gray-500 whitespace-nowrap',
                      'cursor-pointer select-none',
                      'focus:bg-gray-100 focus:outline-none'
                    )}
                  >
                    <SelectPrimitive.ItemText>
                      {clearLabel || placeholder}
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                )}
                {filteredOptions.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={cn(
                      'px-3 py-2 rounded-md max-sm:min-h-[44px] max-sm:flex max-sm:items-center',
                      'text-sm text-gray-700 whitespace-nowrap',
                      'cursor-pointer select-none',
                      'focus:bg-gray-100 focus:outline-none',
                      'data-[state=checked]:bg-soft-mint data-[state=checked]:text-dark-teal data-[state=checked]:font-medium',
                      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed'
                    )}
                  >
                    <SelectPrimitive.ItemText>
                      <span className="flex min-w-0 items-center gap-2">
                        {option.icon && (
                          <i className={`hm hm-${option.icon} flex-shrink-0 text-base`} aria-hidden="true" />
                        )}
                        <span className="min-w-0 truncate">{option.label}</span>
                      </span>
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {error && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
